
import { Json } from "@/integrations/supabase/types";

export interface NotificationSetting {
  key: string;
  label: string;
  description: string;
  email: boolean;
  inApp: boolean;
  frequency: "immediate" | "daily" | "never";
}

export interface EmailSettings {
  email_provider: string;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_secure: boolean;
  from_address: string;
}

export interface GeneralSettings {
  site_name: string;
  site_description: string;
  contact_email: string;
  maintenance_mode: boolean;
  maintenance_message: string;
}

// Helper functions to safely cast JSON to our types
export const asNotificationSettings = (json: Json): NotificationSetting[] => {
  const defaultSettings: NotificationSetting[] = [
    {
      key: 'document_verified',
      label: 'Document Verification',
      description: 'Send notifications when a user\'s document is approved or rejected',
      email: true,
      inApp: true,
      frequency: 'immediate',
    },
    {
      key: 'new_poll_published',
      label: 'New Poll Published',
      description: 'Notify verified pollsters when a new poll is published',
      email: false,
      inApp: true,
      frequency: 'immediate',
    },
    {
      key: 'pollster_status_updated',
      label: 'Pollster Status Updated',
      description: 'Notify users when they are added as a pollster or their status changes',
      email: true,
      inApp: true,
      frequency: 'immediate',
    },
    {
      key: 'poll_closed',
      label: 'Poll Closed',
      description: 'Notify participants when a poll they voted in is closed and analyzed',
      email: false,
      inApp: true,
      frequency: 'immediate',
    },
  ];
  
  // First check if json is an array
  if (!Array.isArray(json)) {
    return defaultSettings;
  }
  
  // Map each item in the array, ensuring type safety
  return json.map((item) => {
    if (typeof item !== 'object' || item === null) {
      return defaultSettings[0];
    }
    
    // Check if item has the frequency property and it's a valid value
    let frequency: "immediate" | "daily" | "never" = 'immediate';
    if (
      item && 
      typeof item === 'object' && 
      'frequency' in item && 
      typeof item.frequency === 'string' && 
      ['immediate', 'daily', 'never'].includes(item.frequency as string)
    ) {
      frequency = item.frequency as "immediate" | "daily" | "never";
    }
    
    return {
      key: typeof item === 'object' && item !== null && 'key' in item && typeof item.key === 'string' ? item.key : '',
      label: typeof item === 'object' && item !== null && 'label' in item && typeof item.label === 'string' ? item.label : '',
      description: typeof item === 'object' && item !== null && 'description' in item && typeof item.description === 'string' ? item.description : '',
      email: typeof item === 'object' && item !== null && 'email' in item && typeof item.email === 'boolean' ? item.email : false,
      inApp: typeof item === 'object' && item !== null && 'inApp' in item && typeof item.inApp === 'boolean' ? item.inApp : false,
      frequency
    };
  });
};

export const asEmailSettings = (json: Json): EmailSettings => {
  const defaultSettings: EmailSettings = {
    email_provider: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_secure: false,
    from_address: ''
  };
  
  if (typeof json !== 'object' || json === null) {
    return defaultSettings;
  }
  
  return {
    email_provider: typeof json === 'object' && json !== null && 'email_provider' in json && typeof json.email_provider === 'string' ? json.email_provider : '',
    smtp_host: typeof json === 'object' && json !== null && 'smtp_host' in json && typeof json.smtp_host === 'string' ? json.smtp_host : '',
    smtp_port: typeof json === 'object' && json !== null && 'smtp_port' in json && typeof json.smtp_port === 'number' ? json.smtp_port : 587,
    smtp_user: typeof json === 'object' && json !== null && 'smtp_user' in json && typeof json.smtp_user === 'string' ? json.smtp_user : '',
    smtp_secure: typeof json === 'object' && json !== null && 'smtp_secure' in json && typeof json.smtp_secure === 'boolean' ? json.smtp_secure : false,
    from_address: typeof json === 'object' && json !== null && 'from_address' in json && typeof json.from_address === 'string' ? json.from_address : ''
  };
};

export const asGeneralSettings = (json: Json): GeneralSettings => {
  const defaultSettings: GeneralSettings = {
    site_name: '',
    site_description: '',
    contact_email: '',
    maintenance_mode: false,
    maintenance_message: ''
  };
  
  if (typeof json !== 'object' || json === null) {
    return defaultSettings;
  }
  
  return {
    site_name: typeof json === 'object' && json !== null && 'site_name' in json && typeof json.site_name === 'string' ? json.site_name : '',
    site_description: typeof json === 'object' && json !== null && 'site_description' in json && typeof json.site_description === 'string' ? json.site_description : '',
    contact_email: typeof json === 'object' && json !== null && 'contact_email' in json && typeof json.contact_email === 'string' ? json.contact_email : '',
    maintenance_mode: typeof json === 'object' && json !== null && 'maintenance_mode' in json && typeof json.maintenance_mode === 'boolean' ? json.maintenance_mode : false,
    maintenance_message: typeof json === 'object' && json !== null && 'maintenance_message' in json && typeof json.maintenance_message === 'string' ? json.maintenance_message : ''
  };
};
